import { useCallback, useEffect, useRef, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

export function PWAUpdatePrompt() {
  const [isUpdating, setIsUpdating] = useState(false);
  const hasReloadedRef = useRef(false);
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;

      // If there is already a waiting worker, prompt immediately.
      if (registration.waiting) {
        setNeedRefresh(true);
      }

      // Run an initial check shortly after startup.
      window.setTimeout(() => {
        registration.update().catch(() => {});
      }, 5000);

      // Check for updates periodically without requiring reinstall.
      const CHECK_INTERVAL_MS = 60 * 60 * 1000; // 60 minutes
      const updateInterval = window.setInterval(() => {
        registration.update().catch(() => {});
      }, CHECK_INTERVAL_MS);

      // Also check when user returns to the app or reconnects.
      const onVisible = () => {
        if (document.visibilityState === 'visible') {
          registration.update().catch(() => {});
        }
      };
      const onOnline = () => registration.update().catch(() => {});

      document.addEventListener('visibilitychange', onVisible);
      window.addEventListener('online', onOnline);

      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            setNeedRefresh(true);
          }
        });
      });

      window.addEventListener('beforeunload', () => {
        window.clearInterval(updateInterval);
        document.removeEventListener('visibilitychange', onVisible);
        window.removeEventListener('online', onOnline);
      });
    },
  });

  useEffect(() => {
    if (!needRefresh) return;

    const handleControllerChange = () => {
      if (hasReloadedRef.current) return;
      hasReloadedRef.current = true;
      window.location.reload();
    };

    navigator.serviceWorker?.addEventListener('controllerchange', handleControllerChange);
    return () => {
      navigator.serviceWorker?.removeEventListener('controllerchange', handleControllerChange);
    };
  }, [needRefresh]);

  useEffect(() => {
    navigator.serviceWorker?.getRegistration?.().then((registration) => {
      if (registration?.waiting) {
        setNeedRefresh(true);
      }
    });
  }, [setNeedRefresh]);

  const handleUpdateNow = useCallback(async () => {
    try {
      setIsUpdating(true);
      await updateServiceWorker(true);
      // Fallback if controllerchange doesn't fire on some devices/browsers.
      window.setTimeout(() => {
        if (!hasReloadedRef.current) {
          hasReloadedRef.current = true;
          window.location.reload();
        }
      }, 3000);
    } catch {
      setIsUpdating(false);
    }
  }, [updateServiceWorker]);

  const handleHardRefresh = useCallback(async () => {
    try {
      setIsUpdating(true);
      await updateServiceWorker(true);

      // Cache-busting reload fallback in case browser doesn't refresh immediately.
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set('refresh', Date.now().toString());
      hasReloadedRef.current = true;
      window.location.replace(nextUrl.toString());
    } catch {
      setIsUpdating(false);
    }
  }, [updateServiceWorker]);

  useEffect(() => {
    if (!needRefresh) return;
    const previousTitle = document.title;
    // Bring prompt back to foreground when update is pending.
    document.title = 'Atualização disponível - Divus';
    return () => {
      document.title = previousTitle;
    };
  }, [needRefresh]);

  if (!needRefresh) return null;

  return (
    <div className="fixed bottom-20 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-[28rem] z-[60] bg-background border border-border rounded-lg shadow-lg p-4">
      <p className="text-sm font-semibold mb-1 text-foreground">Nova versão disponível</p>
      <p className="text-xs text-muted-foreground mb-3">
        Atualize para aplicar correcoes e evitar precisar reinstalar o app.
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={handleUpdateNow}
          disabled={isUpdating}
          className="flex-1 min-w-[10rem] bg-primary text-primary-foreground text-sm py-2 px-3 rounded-md hover:bg-primary/90 transition-colors disabled:opacity-70"
        >
          {isUpdating ? 'Atualizando...' : 'Atualizar agora'}
        </button>
        <button
          onClick={handleHardRefresh}
          disabled={isUpdating}
          className="flex-1 min-w-[10rem] text-sm py-2 px-3 rounded-md border border-border text-foreground hover:bg-muted transition-colors disabled:opacity-70"
        >
          Recarregar limpo
        </button>
        <button
          onClick={() => setNeedRefresh(false)}
          disabled={isUpdating}
          className="text-sm py-2 px-3 rounded-md border border-border text-foreground hover:bg-muted transition-colors disabled:opacity-70"
        >
          Depois
        </button>
      </div>
    </div>
  );
}
